import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Check, X, Receipt, TrendingDown, Calendar, Filter, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const fmt = (n) => (n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CAT_COLORS = {
  loyer: 'bg-blue-100 text-blue-700',
  salaires: 'bg-violet-100 text-violet-700',
  assurances: 'bg-emerald-100 text-emerald-700',
  energie: 'bg-amber-100 text-amber-700',
  vehicules: 'bg-cyan-100 text-cyan-700',
  marketing: 'bg-pink-100 text-pink-700',
  entretien: 'bg-orange-100 text-orange-700',
  licences: 'bg-indigo-100 text-indigo-700',
  formation: 'bg-teal-100 text-teal-700',
  divers: 'bg-slate-100 text-slate-700',
};

const GeneralExpensesPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [filterCat, setFilterCat] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: '', amount: '', category: 'divers', description: '', recurring: false });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filterCat !== 'all') params.category = filterCat;
      const [expRes, catRes, sumRes] = await Promise.all([
        axios.get(`${API}/finance/expenses`, { params }),
        axios.get(`${API}/finance/expense-categories`),
        axios.get(`${API}/finance/expenses/summary`, { params: { year } }),
      ]);
      setExpenses(expRes.data);
      setCategories(catRes.data);
      setSummary(sumRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year, filterCat]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditing(null);
    setForm({ date: new Date().toISOString().slice(0, 10), amount: '', category: 'divers', description: '', recurring: false });
    setShowForm(true);
  };

  const openEdit = (exp) => {
    setEditing(exp);
    setForm({ date: exp.date, amount: String(exp.amount), category: exp.category, description: exp.description, recurring: exp.recurring || false });
    setShowForm(true);
  };

  const saveExpense = async () => {
    if (!form.date || !form.amount) { toast.error('Date et montant requis'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/finance/expenses/${editing.id}`, { ...form, amount: parseFloat(form.amount) });
        toast.success('Charge modifiee');
      } else {
        await axios.post(`${API}/finance/expenses`, { ...form, amount: parseFloat(form.amount) });
        toast.success('Charge ajoutee');
      }
      setShowForm(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    try {
      await axios.delete(`${API}/finance/expenses/${id}`);
      toast.success('Charge supprimee');
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const catMap = Object.fromEntries(categories.map(c => [c.key, c.label]));

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto" data-testid="expenses-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="expenses-title">{t('accounting.general_expenses')}</h1>
            <p className="text-sm text-slate-500">Loyer, salaires, assurances et autres charges</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} data-testid="prev-year-exp">&lt;</Button>
            <span className="text-sm font-bold text-slate-700 min-w-[50px] text-center">{year}</span>
            <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} data-testid="next-year-exp">&gt;</Button>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-40 h-9" data-testid="filter-category">
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="expenses-summary">
            <Card className="p-4 border-l-4 border-l-red-400">
              <p className="text-xs text-slate-500 uppercase">Total {year}</p>
              <p className="text-2xl font-bold text-red-700 mt-1">CHF {fmt(summary.total)}</p>
              <p className="text-xs text-slate-400">{summary.by_category.length} categorie(s)</p>
            </Card>
            <Card className="p-4 border-l-4 border-l-amber-400">
              <p className="text-xs text-slate-500 uppercase">Moy. mensuelle</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">CHF {fmt(summary.total / 12)}</p>
            </Card>
            {summary.by_category.slice(0, 2).map(c => (
              <Card key={c.category} className="p-4">
                <p className="text-xs text-slate-500 uppercase">{c.label}</p>
                <p className="text-xl font-bold text-slate-900 mt-1">CHF {fmt(c.total)}</p>
                <p className="text-xs text-slate-400">{c.count} entree(s)</p>
              </Card>
            ))}
          </div>
        )}

        {/* Category Breakdown */}
        {summary && summary.by_category.length > 0 && (
          <Card className="p-5" data-testid="category-breakdown">
            <h3 className="text-sm font-bold text-slate-900 mb-3">Repartition par categorie</h3>
            <div className="space-y-2">
              {summary.by_category.map(c => {
                const pct = summary.total > 0 ? (c.total / summary.total) * 100 : 0;
                return (
                  <div key={c.category} className="flex items-center gap-3">
                    <Badge className={`text-[10px] min-w-[120px] justify-center ${CAT_COLORS[c.category] || CAT_COLORS.divers}`}>
                      {c.label}
                    </Badge>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-slate-700 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-bold text-slate-900 min-w-[100px] text-right">CHF {fmt(c.total)}</span>
                    <span className="text-xs text-slate-400 min-w-[40px] text-right">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Expenses List */}
        <Card className="overflow-hidden" data-testid="expenses-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Categorie</th>
                  <th className="px-4 py-3 text-left font-medium">{t('common.description')}</th>
                  <th className="px-4 py-3 text-right font-medium">Montant</th>
                  <th className="px-4 py-3 text-center font-medium w-24">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">{t('common.loading')}</td></tr>
                ) : expenses.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400">Aucune charge enregistree pour {year}</td></tr>
                ) : expenses.map((exp, idx) => (
                  <tr key={exp.id} className={`border-b border-slate-100 ${idx % 2 ? 'bg-slate-50/50' : 'bg-white'} hover:bg-slate-50`}
                    data-testid={`expense-row-${exp.id}`}>
                    <td className="px-4 py-2.5 text-slate-700">
                      {new Date(exp.date).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {exp.recurring && <Badge className="ml-1 text-[9px] bg-blue-50 text-blue-500">Recurrent</Badge>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={`text-[10px] ${CAT_COLORS[exp.category] || CAT_COLORS.divers}`}>
                        {catMap[exp.category] || exp.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-xs truncate">{exp.description || '-'}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-red-700">CHF {fmt(exp.amount)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(exp)} className="p-1 rounded hover:bg-slate-200 transition-colors" data-testid={`edit-${exp.id}`}>
                          <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button onClick={() => deleteExpense(exp.id)} className="p-1 rounded hover:bg-red-100 transition-colors" data-testid={`delete-${exp.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la charge' : 'Nouvelle charge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Date</label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="expense-date-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Montant CHF</label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00" data-testid="expense-amount-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Categorie</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t('common.description')}</label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Details..." data-testid="expense-description-input" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))}
                className="rounded" data-testid="expense-recurring-checkbox" />
              Charge recurrente (mensuelle)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
            <Button onClick={saveExpense} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="save-expense-btn">
              {editing ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default GeneralExpensesPage;
